import { useState } from 'react';
import { useRouter } from 'next/router';
import { format, fromUnixTime, isValid, parseISO } from 'date-fns';

import { Anchor, Box, Paragraph, Text } from '@zillow/constellation-10';

import {
  type RentalManagerExternalTourDto,
  RentalManagerExternalTourType,
  type RentalManagerVrModelDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import { typeCheckUtils } from '@zg-rentals/zillow-rental-manager/utils';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import { useTourAssociation } from '../../../../../hooks/useTourAssociation';
import { getDefaultTourLabel } from '../../../../../utils/3dToursUtils';

import NotificationConstants from '../../../../../constants/NotificationConstants';

import SelectTourModal from '../../../../modals/SelectTourModal';
import ViewTourModal from '../../../../modals/ViewTourModal';

import { singleTextLineStyles } from '../../../../../styles/text';

import { useAuth } from '../../../../AuthProvider';
import TourCardContainer from '../../../TourCard/TourCardContainer';
import TourCardContentContainer from '../../../TourCard/TourCardContentContainer';
import TourCardEditableControls from '../../../TourCard/TourCardEditableControls';
import TourCardImage from '../../../TourCard/TourCardImage';

import type { FC } from 'react';
import type { ToastRootPropsInterface } from '@zillow/constellation-10';

type TourCardEditableProps = {
  vrModel?: RentalManagerVrModelDto;
  externalTour?: RentalManagerExternalTourDto;
  tourType: 'amenity' | 'floorPlan' | 'unit' | 'singleFamily';
  hasBigBottomBorder?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  size?: 'sm' | 'md';
  updateStatusMessage: (message: string, type: ToastRootPropsInterface['tone']) => void;
  floorPlanIndex?: number | null;
  unitIndex?: number | null;
};

type AssociateParams = {
  shouldAssociate: boolean;
  tourId?: string;
  vrModelReferenceId?: string;
  externalTour?: RentalManagerExternalTourDto;
};

const isNumeric = typeCheckUtils.isNumeric;

const TourCardEditable: FC<TourCardEditableProps> = ({
  vrModel = {
    averageViewDuration: 0,
    createDateTimeUtc: '',
    creatorName: '',
    creatorZuid: 0,
    label: '',
    posterUrl: '',
    referenceId: '',
    revisionId: '',
    status: '',
    totalTourViews: 0,
    tourUrl: '',
    vrModelGuid: '',
    vrModelId: '',
  },
  externalTour = {
    createdTimestamp: '',
    externalTourType: RentalManagerExternalTourType.ZRM,
    isEditable: false,
    isReadOnly: false,
    label: '',
    referenceId: '',
    sourceName: '',
    status: '',
    tourType: '',
    tourUrl: '',
  },
  tourType,
  hasBigBottomBorder = true,
  isHidden = false,
  size = 'sm',
  floorPlanIndex = null,
  unitIndex = null,
  updateStatusMessage,
}) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { handleAssociate, handleTourError } = useTourAssociation({
    listingAlias,
    listingDetails,
    floorPlanIndex,
    unitIndex,
  });

  const [showViewTourModal, setShowViewTourModal] = useState(false);
  const [showSelectTourModal, setShowSelectTourModal] = useState(false);

  const isExternalTour = !!externalTour.tourUrl;

  const getRemoveAssociateParams = (): AssociateParams => {
    const associateParams: AssociateParams = { shouldAssociate: false };
    if (externalTour.tourUrl) {
      associateParams.externalTour = externalTour;
    } else if (vrModel.vrModelId) {
      associateParams.tourId = vrModel.vrModelId;
      associateParams.vrModelReferenceId = vrModel.referenceId;
    }
    return associateParams;
  };

  const handleRemoveTour = () => {
    const associateParams = getRemoveAssociateParams();
    if (associateParams.tourId || (associateParams.externalTour && associateParams.externalTour.tourUrl)) {
      return handleAssociate(associateParams).catch((err) =>
        updateStatusMessage(err.message || '3D tour couldn’t be removed. Try again.', NotificationConstants.CRITICAL),
      );
    }
    return Promise.resolve();
  };

  const handleAdd = (
    isZillowTour: boolean,
    newSelectedTourId?: string | null,
    newExternalTour?: RentalManagerExternalTourDto,
  ) => {
    if (isZillowTour && newSelectedTourId) {
      return handleAssociate({
        shouldAssociate: true,
        tourId: newSelectedTourId,
      })
        .then(() => {
          setShowViewTourModal(false);
        })
        .catch((err) => Promise.reject(err.message || '3D tour couldn’t be added. Reload the page and try again.'));
    }

    return handleAssociate({
      shouldAssociate: true,
      externalTour: newExternalTour,
    })
      .then(() => {
        setShowViewTourModal(false);
      })
      .catch((error: unknown) => {
        return handleTourError(
          error,
          listingDetails?.isMultifamily,
          user?.isMultifamilyCustomer,
          user?.isSingleFamilyEnterpriseCustomer,
        );
      });
  };

  const handleSelectTourSubmit = (
    isZillowTour: boolean,
    newSelectedTourId?: string | null,
    newExternalTour?: RentalManagerExternalTourDto,
  ) => {
    if (!newSelectedTourId && !newExternalTour?.tourUrl) {
      setShowSelectTourModal(false);
      handleRemoveTour();
      return Promise.resolve();
    }

    const isVrModelUpdated = newSelectedTourId && newSelectedTourId !== vrModel.vrModelId;
    const isExternalTourUpdated =
      newExternalTour &&
      (newExternalTour.tourUrl !== externalTour.tourUrl || newExternalTour.label !== externalTour.label);
    if (isVrModelUpdated || isExternalTourUpdated) {
      handleAssociate(getRemoveAssociateParams()).then(() =>
        handleAdd(isZillowTour, newSelectedTourId, newExternalTour),
      );
      return Promise.resolve();
    }

    setShowSelectTourModal(false);
    return Promise.resolve();
  };

  const handleViewTour = () => {
    if (isExternalTour) {
      window.open(externalTour.tourUrl, '_blank');
    } else {
      setShowViewTourModal(true);
    }
  };

  const renderImage = () => {
    return <TourCardImage imageSrc={vrModel.posterUrl} isHidden={isHidden} size={size} />;
  };

  const renderContent = () => {
    let title: string;
    let createdDate: Date | null = null;
    let source: string;
    let isVrModelStatsVisible: boolean;

    if (isExternalTour) {
      title = externalTour.label || '';
      source = externalTour.sourceName;
      isVrModelStatsVisible = false;
      // externalTour.createdTimestamp is Unix timestamp in milliseconds as string
      if (externalTour.createdTimestamp) {
        createdDate = fromUnixTime(Number(externalTour.createdTimestamp) / 1000);
      }
    } else {
      title = vrModel.label || '';
      source = 'Zillow 3D Home app';
      isVrModelStatsVisible = isNumeric(vrModel.totalTourViews) && Number(vrModel.totalTourViews) > 0;
      // vrModel.createDateTimeUtc is ISO 8601 string
      if (vrModel.createDateTimeUtc) {
        createdDate = parseISO(vrModel.createDateTimeUtc);
      }
    }

    const averageViewDuration = new Date(0);
    averageViewDuration.setSeconds(vrModel.averageViewDuration || 0);

    return (
      <>
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <Paragraph textStyle="body-bold" css={{ ...singleTextLineStyles, flex: 1 }}>
            {title}
          </Paragraph>
          <TourCardEditableControls onRemoveTour={handleRemoveTour} onViewTour={handleViewTour} />
        </Box>
        <Box
          css={{
            padding: '{spacing.200} 0 {spacing.400} 0',
            display: 'flex',
            visibility: isVrModelStatsVisible ? 'visible' : 'hidden',
          }}
        >
          <Box css={{ paddingRight: '200' }}>
            <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
              <b>{vrModel.totalTourViews}</b>
              <Text textStyle="body-xs" color="text.subtle">
                {' views'}
              </Text>
            </Paragraph>
          </Box>
          {isValid(averageViewDuration) && (
            <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
              <b>{format(averageViewDuration, 'm:ss')}</b>
              <Text textStyle="body-xs" color="text.subtle">
                {' avg. viewing time'}
              </Text>
            </Paragraph>
          )}
        </Box>
        <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
          {'Source: '}
          {isExternalTour ? (
            <Anchor
              css={{ textStyle: 'body-sm' }}
              href={externalTour.tourUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {source}
            </Anchor>
          ) : (
            <Anchor asChild css={{ textStyle: 'body-sm' }}>
              <button type="button" onClick={() => setShowViewTourModal(true)}>
                {source}
              </button>
            </Anchor>
          )}
        </Paragraph>
        <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
          {`Created: ${createdDate ? format(createdDate, 'MM/dd/yyyy') : '-'}`}
        </Paragraph>
      </>
    );
  };

  return (
    <>
      <TourCardContainer hasBigBottomBorder={hasBigBottomBorder} size={size}>
        {renderImage()}
        <TourCardContentContainer size={size}>{renderContent()}</TourCardContentContainer>
      </TourCardContainer>
      {showViewTourModal && (
        <ViewTourModal
          vrModel={{ ...vrModel, name: vrModel.label || '', tourUrl: vrModel.tourUrl || '' }}
          onClose={() => setShowViewTourModal(false)}
        />
      )}
      {showSelectTourModal && (
        <SelectTourModal
          tourType={tourType}
          initialSelectedVrModelId={vrModel.vrModelId || null}
          initialExternalTour={{
            ...externalTour,
            label:
              vrModel.vrModelId || externalTour.tourUrl
                ? externalTour.label
                : getDefaultTourLabel(tourType, floorPlanIndex, unitIndex, listingDetails),
          }}
          initialTab={externalTour.tourUrl ? 'external-tour-tab' : 'zillow-tour-tab'}
          onSubmit={handleSelectTourSubmit}
          onClose={() => setShowSelectTourModal(false)}
          zillowDisplayStatus={listingDetails?.syndication?.zillow?.status ?? ''}
          filterOutVrModelIds={listingDetails?.vrModels?.map((vrModel) => vrModel.vrModelId)}
        />
      )}
    </>
  );
};

export type { TourCardEditableProps };
export default TourCardEditable;
